import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import StatsCards from "@/pages/admin/dashboard/components/StatsCards";
import type { StatItem } from "@/pages/admin/dashboard/components/StatsCards";
import ModerationTab from "@/pages/admin/dashboard/components/ModerationTab";
import FeaturedTab from "@/pages/admin/dashboard/components/FeaturedTab";
import UsersTab from "@/pages/admin/dashboard/components/UsersTab";
import ReportsTab from "@/pages/admin/dashboard/components/ReportsTab";
import PromotionsTab from "@/pages/admin/dashboard/components/PromotionsTab";
import TableSkeleton from "@/pages/admin/dashboard/components/TableSkeleton";
import type {
  AdminProduct,
  AdminProfile,
  AdminReport,
} from "@/pages/admin/dashboard/types";

type TabKey = "moderation" | "featured" | "users" | "reports" | "promotions";

interface TabDef {
  key: TabKey;
  label: string;
  icon: string;
}

const TABS: TabDef[] = [
  { key: "moderation", label: "Moderación", icon: "ri-file-list-3-line" },
  { key: "featured", label: "Destacados", icon: "ri-star-line" },
  { key: "users", label: "Usuarios", icon: "ri-team-line" },
  { key: "reports", label: "Reportes", icon: "ri-alert-line" },
  { key: "promotions", label: "Promociones", icon: "ri-megaphone-line" },
];

export default function AdminDashboard() {
  const { profile, user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>("moderation");
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [profiles, setProfiles] = useState<AdminProfile[]>([]);
  const [reports, setReports] = useState<AdminReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pRes, prRes, rRes] = await Promise.all([
        supabase
          .from("products")
          .select("*")
          .order("created_at", { ascending: false }),
        supabase
          .from("profiles")
          .select("*")
          .order("created_at", { ascending: false }),
        supabase
          .from("reports")
          .select("*")
          .order("created_at", { ascending: false }),
      ]);
      if (pRes.error) throw pRes.error;
      if (prRes.error) throw prRes.error;
      if (rRes.error) throw rRes.error;
      setProducts((pRes.data ?? []) as AdminProduct[]);
      setProfiles((prRes.data ?? []) as AdminProfile[]);
      setReports((rRes.data ?? []) as AdminReport[]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "No se pudieron cargar los datos."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const pendingProducts = useMemo(
    () => products.filter((p) => p.status === "pending_moderation"),
    [products]
  );

  const stats: StatItem[] = useMemo(() => {
    const activeUsers = profiles.filter((p) => p.is_active).length;
    const pendingReports = reports.filter((r) => r.status === "pending").length;
    return [
      {
        label: "Usuarios activos",
        value: String(activeUsers),
        icon: "ri-user-line",
        tone: "secondary",
      },
      {
        label: "Publicaciones",
        value: String(products.length),
        icon: "ri-store-2-line",
        tone: "primary",
      },
      {
        label: "En moderación",
        value: String(pendingProducts.length),
        icon: "ri-flag-line",
        tone: "accent",
      },
      {
        label: "Reportes pendientes",
        value: String(pendingReports),
        icon: "ri-alert-line",
        tone: "neutral",
      },
    ];
  }, [profiles, products, reports, pendingProducts]);

  const tabCounts: Record<TabKey, number> = {
    moderation: pendingProducts.length,
    featured: products.filter((p) => p.featured).length,
    users: profiles.length,
    reports: reports.length,
    promotions: 0,
  };

  return (
    <div className="min-h-screen bg-background-50">
      <main className="mx-auto px-4 py-8 md:px-6 lg:max-w-7xl">
        {/* Encabezado */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100">
              <i className="ri-admin-line text-xl text-primary-500"></i>
            </span>
            <div>
              <h1 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                Hola, {profile?.full_name ?? "Administrador"}
                <span className="whitespace-nowrap rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
                  {profile?.role ?? "admin"}
                </span>
              </h1>
              <p className="text-sm text-foreground-600">{profile?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 self-start sm:ml-auto sm:self-auto">
            <Link
              to="/admin/vendedores"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              <i className="ri-store-2-line"></i>
              Aprobar vendedores
            </Link>
            <button
              type="button"
              onClick={fetchAll}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-3 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
              Actualizar
            </button>
          </div>
        </div>

        {/* Métricas */}
        <StatsCards stats={stats} />

        {/* Pestañas */}
        <div className="mt-8">
          <div className="inline-flex items-center gap-1 rounded-full border border-background-200/70 bg-background-100 p-1">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  activeTab === tab.key
                    ? "bg-primary-500 text-white"
                    : "text-foreground-500 hover:text-foreground-900"
                }`}
              >
                <i className={`${tab.icon} text-base`}></i>
                {tab.label}
                <span
                  className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                    activeTab === tab.key
                      ? "bg-white/20 text-white"
                      : "bg-background-200 text-foreground-600"
                  }`}
                >
                  {tabCounts[tab.key]}
                </span>
              </button>
            ))}
          </div>

          {/* Contenido */}
          <div className="mt-4 rounded-lg border border-background-200/70 bg-background-50">
            {activeTab === "promotions" ? (
              <PromotionsTab />
            ) : loading ? (
              <TableSkeleton rows={5} />
            ) : error ? (
              <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-error-warning-line text-2xl text-accent-700"></i>
                </span>
                <p className="mt-4 text-sm font-semibold text-foreground-900">
                  Ocurrió un error
                </p>
                <p className="mt-1 max-w-sm text-sm text-foreground-500">{error}</p>
                <button
                  type="button"
                  onClick={fetchAll}
                  className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
                >
                  Reintentar
                </button>
              </div>
            ) : activeTab === "moderation" ? (
              <ModerationTab
                products={pendingProducts}
                profiles={profiles}
                onRefresh={fetchAll}
              />
            ) : activeTab === "featured" ? (
              <FeaturedTab products={products} onRefresh={fetchAll} />
            ) : activeTab === "users" ? (
              <UsersTab
                profiles={profiles}
                currentUserId={user?.id}
                onRefresh={fetchAll}
              />
            ) : (
              <ReportsTab
                reports={reports}
                products={products}
                profiles={profiles}
                onRefresh={fetchAll}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}